import { useEffect } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  Outlet,
  HeadContent,
  Scripts,
} from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { Toaster } from 'sonner';
import '@/i18n';
import kanitLight from '@/assets/fonts/Kanit-Light.woff2?url';
import kanitMedium from '@/assets/fonts/Kanit-Medium.woff2?url';
import kanitSemiBold from '@/assets/fonts/Kanit-SemiBold.woff2?url';
import { QueryProvider } from '@/providers/QueryProvider';
import { useAuthStore } from '@/stores/authStore';
import { NotFoundPage } from '@/pages/NotFound';
import globalCss from '@/styles/global.css?url';

function AuthInitializer() {
  const checkAuth = useAuthStore((state) => state.checkAuth);

  useEffect(() => {
    checkAuth().catch((error) => {
      console.error('[AuthInitializer] Failed to load user profile:', error);
    });
  }, []);

  return null;
}

function RootComponent() {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        <QueryProvider>
          <AuthInitializer />
          <Outlet />
          <TanStackRouterDevtools position="bottom-right" />
        </QueryProvider>
        {/* Toaster is outside QueryProvider and all layout containers so
            position:fixed is always relative to the viewport, not a transform ancestor */}
        <Toaster position="top-center" richColors offset={36} />
        <Scripts />
      </body>
    </html>
  );
}

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  head: () => ({
    meta: [
      { charSet: 'UTF-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1.0' },
      { title: 'G&T Engine Parts - Admin Portal' },
      { name: 'robots', content: 'noindex, nofollow' },
    ],
    links: [
      { rel: 'preload', as: 'font', type: 'font/woff2', href: kanitLight, crossOrigin: 'anonymous' },
      { rel: 'preload', as: 'font', type: 'font/woff2', href: kanitMedium, crossOrigin: 'anonymous' },
      { rel: 'preload', as: 'font', type: 'font/woff2', href: kanitSemiBold, crossOrigin: 'anonymous' },
      { rel: 'preload', as: 'style', href: globalCss },
      { rel: 'stylesheet', href: globalCss },
      { rel: 'icon', href: '/favicon.ico' },
    ],
  }),
  component: RootComponent,
  notFoundComponent: NotFoundPage,
});